// Document-level keyboard grammar (11 §7, C6). Ignored while focus is in a text field and off the
// encounter screen. Space = ready/unready, P = pause/resume, through the module's reducers.
import { useEffect } from 'react';
import { keyToCommand } from '../core/authoring/keyboard';
import { useAuthoring } from '../core/stores/authoring';
import { useView } from '../core/stores/view';
import { useRoute } from '../core/stores/route';
import { getConn } from '../core/net/connection';
import { rpc } from '../core/net/reducers';
import { useEncounter } from '../core/encounter/session';
import { playSfx } from '../core/audio';
import { pingBoard } from '../core/encounter/pings';
import { undoLast } from './encounter/ActionQueue';
import { openHeroSheet } from './heroes/heroSheetStore';
import { openInventory } from './inventory/store';
import { rangerOf } from '../core/sim/companion';
import { usePlayback } from '../core/stores/playback';

export function toggleReady(): void {
  const { encounterId, me } = useEncounter.getState();
  const c = getConn();
  if (!encounterId || !c) return;
  // the turn is playing out: the next one's plan is not shown yet, so there is nothing to lock
  if (usePlayback.getState().playing) return;
  const row = [...c.db.readyState.iter()].find((r) => r.encounterId === encounterId && r.owner.toHexString() === me);
  // Lock plan commits every hero of mine (the server fills their empty slots with Hold)
  if (row?.ready) { void rpc.unready(encounterId); playSfx('plan.clear'); } else { void rpc.ready(encounterId); playSfx('plan.commit'); }
}

export function togglePause(): void {
  const { encounterId } = useEncounter.getState();
  const c = getConn();
  if (!encounterId || !c) return;
  const ps = [...c.db.pauseState.iter()].find((r) => r.encounterId === encounterId);
  void (ps?.paused ? rpc.resume(encounterId) : rpc.pause(encounterId));
}

/** docs/84: what the keyboard still does over the Spoils: look at the board, nothing else. */
const SPOILS_VIEW: ReadonlySet<string> = new Set(['zoom', 'frame', 'cycleFilter', 'toggleMinimap']);

export function useKeyboard(): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // docs/84: over the Spoils the board still pans and zooms, but nothing is planned
      const screen = useRoute.getState().screen;
      if (screen !== 'encounter' && screen !== 'spoils') return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) return;
      // a dialog over the board (the hero sheet, the inventory, the menu) has the keys
      if (document.querySelector('[aria-modal="true"]')) return;
      const cmd = keyToCommand(e);
      if (!cmd) return;
      if (screen === 'spoils' && !(cmd.kind === 'view' && SPOILS_VIEW.has(cmd.command.type))) return;
      const a = useAuthoring.getState();
      if (cmd.kind === 'authoring') { e.preventDefault(); a.send(cmd.event); return; }
      if (cmd.kind === 'hotkey') {
        // docs/33 E3: the number keys are the hotbar's, as drawn (the open slot's list, else the whole bar)
        const st = a.machineState;
        if (st.kind === 'Idle') return;
        const bar = st.kind === 'SlotChosen' || st.kind === 'ActionChosen' ? a.hotbar : a.barActions();
        // a number always means the same action: one the open slot can't hold (Guard while slot 2 is
        // open) is planned in the slot that can, as a click on its tile is
        const def = bar.find((h) => h.hotkey === cmd.key) ?? (bar === a.hotbar ? a.barActions().find((h) => h.hotkey === cmd.key && h.id !== 2) : undefined);
        if (!def) return;
        if (def.id === 2 && a.slot !== undefined) { a.send({ type: 'hold' }); return; }
        // docs/40: Use Item's number opens the bag, where the item is chosen
        if (def.args === 'item') { useView.getState().toggleInventory(true); return; }
        a.pickAction(def.id);
        return;
      }
      const v = useView.getState();
      switch (cmd.command.type) {
        case 'zoom': v.zoomBy(cmd.command.delta); break;
        case 'frame': v.frameBoard(cmd.command.kind); break;
        case 'cycleFilter': v.cycleFilter(); break;
        case 'toggleMinimap': v.toggleMinimap(); break;
        case 'toggleReady': e.preventDefault(); toggleReady(); break;
        case 'togglePause': togglePause(); break;
        case 'toggleInventory': v.toggleInventory(); break;
        case 'ping': pingBoard(a.cursor, cmd.command.danger ? 2 : 0); break;
        case 'undo': e.preventDefault(); undoLast(); break;
        case 'heroSheet': if (a.selectedHero !== undefined) openHeroSheet(BigInt(rangerOf(a.selectedHero))); break;
        case 'heroGear': if (a.selectedHero !== undefined) openInventory(BigInt(rangerOf(a.selectedHero))); break;
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
}
